import { useEffect, useState } from "react";
import { http, nf } from "@/lib/api";
import { PageHeader, Stat, KatBadge } from "@/components/Ui";
import ExportButtons from "@/components/ExportButtons";
import AiInsight from "@/components/AiInsight";
import AlertEmailSettings from "@/components/AlertEmailSettings";
import { AlertTriangle } from "lucide-react";

const STATUS_STYLE = {
  Kritis: "bg-red-50 text-[#b91c1c] border-[#FF3B30]",
  Waspada: "bg-amber-50 text-amber-700 border-amber-400",
  Aman: "bg-green-50 text-green-700 border-green-400",
  "Tanpa Penjualan": "bg-neutral-100 text-neutral-600 border-neutral-300",
};

export default function StockAlerts() {
  const [d, setD] = useState(null);
  const [filter, setFilter] = useState("Kritis+Waspada");

  useEffect(() => {
    http.get("/alerts/stock").then((r) => setD(r.data)).catch(() => setD({ rows: [] }));
  }, []);

  if (!d) return <div className="p-8 mono text-sm">Memuat peringatan…</div>;

  const rows = d.rows.filter((r) =>
    filter === "Semua" ? true : filter === "Kritis+Waspada" ? ["Kritis", "Waspada"].includes(r.status) : r.status === filter
  );
  const sel = "h-10 px-3 border border-neutral-300 bg-white text-sm outline-none focus:ring-2 focus:ring-[#4338CA]";

  return (
    <div data-testid="stock-alerts-page">
      <PageHeader subtitle="Peringatan Dini" title="Alarm Stock Minim vs Rencana Kedatangan" testId="stock-alerts-header">
        <select data-testid="alerts-status-filter" className={sel} value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="Kritis+Waspada">Kritis &amp; Waspada</option>
          <option value="Kritis">Hanya Kritis</option>
          <option value="Waspada">Hanya Waspada</option>
          <option value="Aman">Aman</option>
          <option value="Tanpa Penjualan">Tanpa Penjualan</option>
          <option value="Semua">Semua</option>
        </select>
        <ExportButtons kind="alerts" testId="export-alerts" />
      </PageHeader>

      <div className="p-6 sm:p-8 space-y-4">
        <AlertEmailSettings />

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Stat testId="alerts-kritis" label="Status Kritis" value={nf(d.kritis)} sub="stock habis sebelum barang tiba" accent="#FF3B30" />
          <Stat testId="alerts-waspada" label="Status Waspada" value={nf(d.waspada)} sub="cover di bawah 2 bulan" accent="#B45309" />
          <Stat testId="alerts-aman" label="Status Aman" value={nf(d.aman)} />
          <Stat testId="alerts-tanpa" label="Tanpa Penjualan" value={nf(d.tanpa_penjualan)} />
        </div>

        {d.kritis > 0 && (
          <div data-testid="alerts-banner" className="border border-[#FF3B30] bg-red-50 px-4 py-3 flex items-start gap-3">
            <AlertTriangle className="h-4 w-4 text-[#b91c1c] mt-0.5" />
            <div className="text-sm text-[#7f1d1d]">
              <strong>{d.kritis} kombinasi produk/lokasi berstatus kritis.</strong> Stock
              diprediksi habis sebelum kedatangan barang berikutnya berdasarkan
              rata-rata penjualan 3 bulan terakhir. Segera koordinasikan realokasi
              stock antar gudang atau percepat ETA.
            </div>
          </div>
        )}

        <div className="erp-panel overflow-auto max-h-[70vh]" data-testid="alerts-table-wrap">
          <table className="erp-table">
            <thead>
              <tr>
                <th>Status</th><th>Kode Stock</th><th>Deskripsi</th><th>Lokasi</th>
                <th className="num">Qty Stock</th><th className="num">Rata Jual/Bulan</th>
                <th className="num">Bulan Cover</th><th>ETA Terdekat</th>
                <th className="num">Hari ke ETA</th><th className="num">Qty ETA</th>
                <th>Supplier</th><th>Kategori</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} data-testid={`alerts-row-${r.kode_stock}-${r.lokasi}`}>
                  <td>
                    <span className={`inline-block border px-1.5 py-0.5 text-[10px] uppercase tracking-wide ${STATUS_STYLE[r.status]}`}>
                      {r.status}
                    </span>
                  </td>
                  <td className="mono font-medium">{r.kode_stock}</td>
                  <td className="max-w-[200px] truncate">{r.deskripsi_produk}</td>
                  <td>{r.lokasi}</td>
                  <td className="num">{nf(r.qty_stock)}</td>
                  <td className="num">{nf(r.rata_penjualan_bulanan)}</td>
                  <td className="num font-semibold">{r.bulan_cover ?? "—"}</td>
                  <td className="mono">{r.eta_terdekat || "—"}</td>
                  <td className="num">{r.hari_ke_eta ?? "—"}</td>
                  <td className="num">{nf(r.qty_eta)}</td>
                  <td>{r.supplier || "—"}</td>
                  <td><KatBadge value={r.kategori_pemasaran} /></td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={12} className="text-center text-neutral-500 py-6">Tidak ada baris untuk filter ini</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <AiInsight scope="peringatan-stock" />
      </div>
    </div>
  );
}
